// Catalog.ID Client — `decap-chat-epoch`: the chat epoch oracle hop
// ============================================================================
// Custody surface for chat's message crypto (chat migration §3). The keybag is
// a root of certification and derivation, NEVER an operational key: bulk
// message encrypt/decrypt runs on epoch keys hot in the chat module, and the
// only thing that ever touches `chat_encrypt` private bytes is this one hop —
// ONE keybag oracle call per epoch per device, after which chat re-wraps the
// epoch key to machine-local custody. The private key never leaves this tab.
//
// WHAT RIDES THIS MODE, and why it is one mode and not two
// -------------------------------------------------------
// Two AAD families, one HKDF info (message-wire.md §5, §6):
//   * conversation epochs — aad = utf8("<operatorId>/<channelId>/<epoch>")
//     (contact-flow.md §5: epoch-1 rides the contact-accept, later epochs ride
//     `chat-epoch-wrap` direct envelopes);
//   * the read-state SYNC key — aad = utf8("<cid>/chat-sync/<keyId>"), which
//     message-wire §6 defines as formally an epoch key of the self sync
//     stream, minted and wrapped with the standard epoch-wrap construction.
// Both wrap under `catalog-chat-epoch-wrap-v1`, so both come through here and
// this handler's allowlist stays SINGLE-ENTRY. That is the point: an allowlist
// with one member cannot be talked into opening somebody else's blob.
//
// WHY THE ALLOWLIST IS NOT SHARED WITH THE EFS HANDLER
// ---------------------------------------------------
// efs-handlers.ts runs the byte-identical construction against `efs_encrypt`
// under `catalog-efs-wrap-v1`. Merging the two allowlists — or adding chat's
// info to the EFS one — would let a blob minted for one purpose be fed to a
// decapsulator built for the other. The whole reason chat has its own keys is
// compromise scoping between mail, storage and chat; sharing a KDF label gives
// some of it back (contact-flow.md §5). Two handlers, two one-entry lists.
//
// WHY THERE IS NO CONSENT MODAL
// -----------------------------
// Same posture as the EFS decap hop: this is a background data-plane call
// inside flows the user started elsewhere (opening a conversation, catching up
// on an offline mailbox). The gate is the keybag UNLOCK, plus the hub's
// `_senderServiceId` stamp, the relay's MANDATORY module-identity signature
// check (`decap-chat-epoch` is pinned in `MandatoryVerifiedInboundModes` from
// day one — chat migration §17), and the digest re-binding below. A modal per
// epoch would fire on every conversation on every device and train users to
// click through exactly the prompt that matters.
//
// Gate stack (post-relay-verification; the relay's gates are already enforced
// server-side):
//   1–3. sender stamp / requester / active identity — `dispatchChatRequest`.
//   4.   required fields present.
//   5.   `hkdfInfo` is on the chat allowlist — BEFORE anything touches keybag
//        material, and before the unlock wait, so a domain-confusion probe
//        learns nothing about lock state and cannot queue against an unlock.
//   6.   wait-for-unlock (M2c): a locked keybag raises this tab through the
//        hub and waits out the shared five-minute budget, whatever the
//        sender's flag says. Chat still sends `false` — nothing here shows a
//        dialog — and `cid-locked` is now the answer only when the member
//        never unlocked, which §3 accepts.
//   7.   wrapped-key digest re-binding: the signed canonical commits only to
//        SHA-512 of the blob, so recompute it or a relay-verified envelope
//        could be paired with a substituted body.
//   8.   AAD digest re-binding — same role for the AAD half.
//   9.   locate the recipient key in the `chat_encrypt` chain, retiring
//        generations included (a rotation must not orphan old epochs).
//  10.   `kemDecapsulate`. The AEAD is the final witness: a wrap minted for
//        another channel, another epoch, another info or another key fails
//        authentication rather than needing a comparison someone can forget.
// ============================================================================

import { sha512 } from '@noble/hashes/sha2.js';
import { keys } from '../state/keys';
import { recordKeyUse } from '../state/key-use-record';
import * as aes from '../crypto/aes';
import { kemDecapsulate, HKDF_INFO_CHAT_EPOCH_WRAP } from '../crypto/kem';
import { findKemKeyByFingerprint } from './kem-key-chain';
import type { ChatRequest, ChatResponse, ChatDispatchDeps } from './chat-types';
import { ensureUnlocked } from './unlock-gate';

/**
 * Keybag slot holding the account's hybrid X25519 + ML-KEM-1024 chat
 * encryption key (migration §3). Multi-device enrollment and rotation ride
 * keybag custody; org delegates are entitled to it from day one.
 */
export const CHAT_ENCRYPT_KEY_ID = 'chat_encrypt';

/**
 * Allowlist of HKDF `info` strings this handler will decapsulate under.
 *
 * SINGLE ENTRY, DELIBERATELY, AND NOT SHARED. Do not add `catalog-efs-wrap-v1`
 * or `catalog-hybrid-kem-v1` here, and do not add this value to the EFS
 * handler's list: each addition is a domain-confusion hole, letting a blob
 * minted for one protocol be opened by a decapsulator built for another. Both
 * chat AAD families (conversation epochs and the read-state sync stream) share
 * this one info by design, so read-state sync needs no second entry.
 */
const ALLOWED_HKDF_INFOS: ReadonlyArray<string> = [
    new TextDecoder().decode(HKDF_INFO_CHAT_EPOCH_WRAP),
];

type ResponseBase = Pick<ChatResponse, 'type' | 'to' | 'requestId'>;

function unavailable(base: ResponseBase, reason: string): ChatResponse {
    return { ...base, status: 'unavailable', reason };
}

export async function handleDecapChatEpoch(
    request: ChatRequest,
    base: ResponseBase,
    deps: ChatDispatchDeps,
): Promise<ChatResponse> {
    // Gate 4.
    if (!request.recipientPubkeyDigest || !request.wrappedKeyBase64
        || !request.wrappedKeyDigestBase64
        || request.hkdfInfo === undefined
        || request.aadBase64 === undefined
        || request.aadDigestBase64 === undefined) {
        return unavailable(base, 'missing-required-field');
    }

    // Gate 5: HKDF info allowlist, ahead of every keybag interaction.
    if (!ALLOWED_HKDF_INFOS.includes(request.hkdfInfo)) {
        return unavailable(base, 'hkdf-info-not-permitted');
    }

    // Gate 6: the universal unlock redirect (M2c). Chat's epoch hop declares
    // `requiresUserInteraction:false` — it shows no dialog — but a locked bag
    // still raises this tab and waits, because a background flow the member
    // started elsewhere is exactly what the ruling is about.
    if (!await ensureUnlocked(request, deps)) return unavailable(base, 'cid-locked');

    let wrappedKey: Uint8Array;
    try {
        wrappedKey = aes.fromBase64(request.wrappedKeyBase64);
    } catch {
        return unavailable(base, 'bad-wrapped-key-base64');
    }

    let aad: Uint8Array;
    try {
        aad = request.aadBase64 === '' ? new Uint8Array(0) : aes.fromBase64(request.aadBase64);
    } catch {
        return unavailable(base, 'bad-aad-base64');
    }

    // Gate 7: wrapped-key digest binding.
    if (aes.toBase64(sha512(wrappedKey)) !== request.wrappedKeyDigestBase64) {
        return unavailable(base, 'wrapped-key-digest-mismatch');
    }

    // Gate 8: AAD digest binding. For chat this is what pins the channel and
    // epoch (or the sync keyId) the relay actually vouched for.
    if (aes.toBase64(sha512(aad)) !== request.aadDigestBase64) {
        return unavailable(base, 'aad-digest-mismatch');
    }

    // Gate 9: locate the recipient key, retiring generations included.
    let match = findKemKeyByFingerprint(CHAT_ENCRYPT_KEY_ID, request.recipientPubkeyDigest);

    // Gate 9b: ABSENT IS NOT LOCKED (device-setup-restructure §1 item 12,
    // §9.2). An unlocked bag with NO `chat_encrypt` chain at all means the
    // member keeps this key off this device (whitepaper §3.4.7) — a different
    // thing from a wrap addressed to a generation this bag never held, which
    // keeps answering `key-not-in-bag`. Chat's epoch hop is non-interactive by
    // design, so in practice it refuses here; the interactive variant (and any
    // later caller that refocuses the tab) can load the key on demand.
    if (!match && keys.getKeyChain(CHAT_ENCRYPT_KEY_ID).length === 0) {
        if (request.requiresUserInteraction === true && deps.requireKey) {
            try {
                await deps.requireKey(CHAT_ENCRYPT_KEY_ID, 'open this conversation');
            } catch {
                // A failed load is an absent key; the refusal below says so.
            }
            match = findKemKeyByFingerprint(CHAT_ENCRYPT_KEY_ID, request.recipientPubkeyDigest);
        }
        if (!match) return unavailable(base, 'key-not-on-device');
    }

    if (!match) {
        return unavailable(base, 'key-not-in-bag');
    }

    // Gate 10: decap. Every failure inside the hybrid construction — wrong key,
    // corrupted ciphertext, bad version byte, mismatched info, mismatched AAD —
    // surfaces as one code so chat branches on `decap-failed` rather than
    // guessing the cause (and so the handler leaks no oracle detail).
    const info = new TextEncoder().encode(request.hkdfInfo);
    let plaintext: Uint8Array;
    try {
        plaintext = await kemDecapsulate(match.secretKey, wrappedKey, info, aad);
    } catch (err) {
        const detail = err instanceof Error ? err.message.slice(0, 80) : 'unknown';
        return unavailable(base, 'decap-failed:' + detail);
    }

    // Evidence of use (§9.4): the operation succeeded, and it was an oracle —
    // only the epoch key leaves, never `chat_encrypt` itself.
    const accountCid = deps.getActiveCid();
    if (accountCid) {
        recordKeyUse(accountCid, CHAT_ENCRYPT_KEY_ID, {
            requesterId: request.requesterId,
            mode: request.mode,
            kind: 'oracle',
        });
    }

    return {
        ...base,
        status: 'approved',
        plaintextBase64: aes.toBase64(plaintext),
    };
}
